import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"

import { cn } from "@/lib/utils"
import { WebappRoutes } from "@/shared/lib/webapp-routes"

import { isSoulmateReport } from "@/features/soulmate-report"

import { ReportIds } from "../../config/reports-data"
import { useUserReports } from "../../hooks/use-user-reports"

import RevealMale from "@/assets/img/soulmate/report/reveal-male.webp"
import RevealFemale from "@/assets/img/soulmate/report/reveal-female.webp"
import CloseIcon from "@/assets/svg/soulmate/report/close-circle.svg?react"

/** Slide-down duration before the sheet unmounts (matches CSS astro-sheet-down). */
const CLOSE_MS = 300

/**
 * "Your Soulmate Sketch Is Ready to Reveal" bottom sheet — shown the first time
 * the user taps the finished Soulmate Sketch report. A blurred teaser of the
 * sketch (male/female per the report's portrait), a lead-in, and a "Reveal my
 * results" CTA that opens the native report page. Dismiss via the X or backdrop.
 */
export function SoulmateReadyPopover({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const { byType } = useUserReports()
  const [closing, setClosing] = useState(false)

  const soulmateContent = byType(ReportIds.Soulmate)?.content
  const gender =
    soulmateContent && isSoulmateReport(soulmateContent) ? soulmateContent.portrait.gender : undefined
  const hero = gender === "male" ? RevealMale : RevealFemale

  // Play the slide-down, then unmount — mirrors the open.
  const close = () => {
    if (closing) return
    setClosing(true)
    window.setTimeout(onClose, CLOSE_MS)
  }

  const open = () => {
    navigate({ to: WebappRoutes.SOULMATE })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[1150]">
      <button
        type="button"
        aria-label="Close"
        onClick={close}
        className={cn(
          "absolute inset-0 h-full w-full bg-dark-base/80 backdrop-blur-sm",
          closing ? "astro-sheet-fade-out" : "astro-sheet-fade",
        )}
      />

      <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-app">
        <div
          data-slot="soulmate-ready-popover"
          className={cn(
            // Purple top glow (box-shadow) matches the other report sheets;
            // bg-dark-base is the fallback behind the full-bleed sketch.
            "relative overflow-hidden rounded-t-[28px] bg-dark-base shadow-[0_-8px_40px_rgba(100,30,220,0.35)]",
            closing ? "astro-sheet-down" : "astro-sheet-up",
          )}
        >
          {/* Full-bleed blurred sketch (with the baked "?") covers the sheet. */}
          <img src={hero} alt="Your blurred soulmate sketch" className="w-full" />

          <button
            type="button"
            aria-label="Close"
            onClick={close}
            className="absolute right-4 top-4 z-10 flex items-center justify-center [&_svg]:size-6"
          >
            <CloseIcon />
          </button>

          {/* Title/subtitle/CTA overlaid on the sketch, over a fade for legibility. */}
          <div className="absolute inset-x-0 bottom-0 flex flex-col items-center bg-gradient-to-b from-transparent via-dark-base/90 to-dark-base px-6 pb-6 pt-28">
            <h2 className="text-center font-display text-[26px] font-medium leading-tight text-purple-light">
              Your Soulmate Sketch Is
              <br />
              Ready to Reveal
            </h2>
            <p className="mt-3 text-center font-body text-[15px] leading-snug text-purple-light/70">
              Your personal sketch and reading are ready. Open it now to see what was revealed for
              you.
            </p>

            <button
              type="button"
              onClick={open}
              className="mt-5 h-14 w-full rounded-full bg-gradient-to-r from-peach to-purple-accent font-body text-base font-semibold text-white"
            >
              Reveal my results
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
